import React, { useState, useRef, memo, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import 'katex/dist/katex.min.css';
import axios from 'axios';
import { toast } from 'react-toastify';
import katex from 'katex';
import { API_BASE_URL } from '../../config';

window.katex = katex;

// Minimal Image Resize Module for Quill


// Add global styles for inline images in Quill and rendered output
const inlineImageStyle = `
  .ql-editor img {
    display: inline-block !important;
    vertical-align: middle;
    margin: 0 4px;
    max-width: 100%;
  }
  .prose img {
    display: inline-block !important;
    vertical-align: middle;
    margin: 0 4px;
    max-width: 100%;
  }
`;

// Symbol Dropdown Component
const SymbolPicker = memo(({ onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const symbols = [
    'α', 'β', 'γ', 'δ', 'ε', 'ζ', 'η', 'θ', 'ι', 'κ', 'λ', 'μ', 'ν', 'ξ', 'ο', 'π', 'ρ', 'σ', 'τ', 'υ', 'φ', 'χ', 'ψ', 'ω',
    'Γ', 'Δ', 'Θ', 'Λ', 'Ξ', 'Π', 'Σ', 'Φ', 'Ψ', 'Ω',
    '±', '÷', '×', '√', '∞', '≈', '≠', '≤', '≥', '∴', '∵', '∫', '∂', '∆', '∇', '∑', '∏', '∩', '∪', '°', 'Å', 'λ'
  ];

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-t-lg border-x border-t border-slate-700 transition-colors"
      >
        <span className="text-blue-400">Ω</span> Symbols
        <svg className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setIsOpen(false)} />
          <div className="absolute left-0 bottom-full z-[70] mb-0 w-64 p-3 bg-slate-900 border border-slate-700 rounded-xl rounded-bl-none shadow-2xl animate-in fade-in slide-in-from-bottom-2">
            <div className="grid grid-cols-6 gap-2 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
              {symbols.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    onSelect(s);
                    // Keep open for multi-selection, or close? 
                    // Let's keep it open but provide a close button or click-outside
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded bg-slate-800 hover:bg-blue-500 hover:text-white text-slate-300 transition-colors text-sm font-medium"
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="mt-2 pt-2 border-t border-slate-800 flex justify-end">
              <button 
                type="button" 
                onClick={() => setIsOpen(false)}
                className="text-[10px] text-slate-500 hover:text-slate-300 uppercase tracking-widest font-bold"
              >
                Close
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
});

const HtmlEditor = memo(({
  editorId,
  value,
  onChange,
  onFocus,
  registerRef,
  placeholder,
  minHeightClass = 'min-h-[120px]',
  paddingClass = 'p-4',
  textClassName = 'text-base'
}) => {
  const editorRef = useRef(null);

  React.useEffect(() => {
    const el = editorRef.current;
    const nextValue = value || '';

    if (el && el.innerHTML !== nextValue) {
      el.innerHTML = nextValue;
    }
  }, [value]);

  const setRefs = useCallback((el) => {
    editorRef.current = el;
    if (registerRef) {
      registerRef(el);
    }
  }, [registerRef]);

  return (
    <div className="relative">
      {!value && placeholder && (
        <div className={`pointer-events-none absolute left-4 top-4 z-10 text-slate-500 ${textClassName}`}>
          {placeholder}
        </div>
      )}
      <div
        ref={setRefs}
        data-editor-id={editorId}
        contentEditable
        suppressContentEditableWarning
        onFocus={onFocus}
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        className={`w-full bg-transparent ${paddingClass} ${minHeightClass} ${textClassName} text-slate-200 outline-none overflow-x-auto whitespace-pre-wrap break-words [&_p]:my-0 [&_img]:inline-block [&_img]:align-middle [&_img]:max-w-full`}
      />
    </div>
  );
});

// Memoized Question Item to prevent list re-renders
const QuestionItem = memo(({ q, onEdit, onDelete, renderRichText }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="p-5 rounded-2xl border border-slate-800 bg-slate-900/40 hover:border-slate-700 transition-all duration-300">
      <div className="flex items-start justify-between mb-3">
        <div className="flex flex-wrap gap-2 text-[10px] font-semibold uppercase tracking-wider">
          <span className="px-2 py-1 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">{q.subject}</span>
          {q.chapter && <span className="px-2 py-1 rounded bg-slate-800 text-slate-400">{q.chapter}</span>}
          <span className="px-2 py-1 rounded bg-slate-800 text-slate-400">Diff: {q.difficulty}</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => onEdit(q)} className="text-slate-500 hover:text-blue-400 transition-colors p-1" title="Edit Question">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
          </button>
          <button onClick={() => onDelete(q.id)} className="text-slate-500 hover:text-rose-400 transition-colors p-1" title="Delete Question">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
          </button>
        </div>
      </div>
      <div className="text-slate-200 font-medium mb-4">
        <div 
          className="prose prose-invert max-w-none prose-sm [&_p]:m-0"
          dangerouslySetInnerHTML={renderRichText(q.text)} 
        />
      </div>



      {q.questionType === 'Integer Type' ? (
        <div className="mt-4 p-5 rounded-2xl border border-dashed border-slate-800 bg-slate-950/20 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Correct Answer</span>
            <div className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-400 font-mono font-bold text-lg shadow-inner ring-1 ring-emerald-500/20">
              {q.correctOption}
            </div>
          </div>
          <div className="h-12 w-full rounded-xl bg-slate-950/40 border border-slate-800/50 flex items-center justify-center">
            <span className="text-slate-600 text-xs italic tracking-tighter">Student Answer Field Placeholder</span>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
          {q.options.map((opt, idx) => (
            <div key={idx} className={`p-3 rounded-xl border text-sm flex items-center gap-2 ${
              (q.questionType === 'Multiple Correct' ? q.correctOption?.includes(String.fromCharCode(65 + idx)) : q.correctOptionIndex === idx)
                ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-200' 
                : 'border-slate-800 bg-slate-950/50 text-slate-400'
            }`}>
              <span className="font-medium opacity-60 w-5">{String.fromCharCode(65 + idx)}.</span> 
              <span 
                className="flex-1 overflow-x-auto hide-scrollbar prose prose-invert prose-sm [&_p]:inline"
                dangerouslySetInnerHTML={renderRichText(opt)}
              />
              {(q.questionType === 'Multiple Correct' ? q.correctOption?.includes(String.fromCharCode(65 + idx)) : q.correctOptionIndex === idx) && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500"><polyline points="20 6 9 17 4 12"></polyline></svg>
              )}
            </div>
          ))}
        </div>
      )}
      {(q.topic || q.customTags || q.questionType) && (
        <div className="mt-4 pt-4 border-t border-slate-800/60 flex flex-wrap gap-3 items-center">
          {q.questionType && (
            <span className="text-[10px] font-bold text-blue-400 bg-blue-500/10 px-2 py-1 rounded-md border border-blue-500/20 uppercase tracking-widest">
              {q.questionType}
            </span>
          )}
          {q.topic && (
            <span className="text-xs text-slate-500 bg-slate-900 px-2 py-1 rounded-md border border-slate-800/80">
              Topic: {q.topic}
            </span>
          )}
          {q.customTags && q.customTags.split(',').map(tag => tag.trim()).filter(Boolean).map((tag, idx) => (
            <span key={idx} className="text-xs text-emerald-400/80 bg-emerald-500/5 px-2 py-1 rounded-md border border-emerald-500/10">
              #{tag}
            </span>
          ))}
        </div>
      )}

      {q.solution && (
        <div className="mt-6 p-4 rounded-xl bg-slate-950/30 border border-slate-800/50 transition-all duration-300">
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-between text-[11px] font-bold text-blue-400 uppercase tracking-widest mb-2 group"
          >
            <div className="flex items-center gap-2">
              <svg 
                className={`transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`}
                width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
              >
                <path d="m9 18 6-6-6-6"/>
              </svg>
              <span>Solution / Explanation</span>
            </div>
            <span className="text-[9px] text-slate-500 font-medium lowercase opacity-0 group-hover:opacity-100 transition-opacity">
              {isExpanded ? 'click to hide' : 'click to reveal'}
            </span>
          </button>
          
          {isExpanded && (
            <div 
              className="prose prose-invert max-w-none prose-sm text-slate-400 mt-3 animate-in fade-in slide-in-from-top-1 duration-300"
              dangerouslySetInnerHTML={renderRichText(q.solution)} 
            />
          )}
        </div>
      )}
    </div>
  );
});

// Sub-components
const VideoLectures = () => {
  const [videos, setVideos] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({ title: '', subject: '', difficulty: 'Medium' });
  const [file, setFile] = useState(null);

  React.useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = () => {
    axios.get(API_BASE_URL + '/api/files/type/Video')
      .then(res => setVideos(res.data))
      .catch(err => console.error("Error fetching videos:", err));
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this video?")) {
      axios.delete(`${API_BASE_URL}/api/files/${id}`)
        .then(() => {
          toast.success("Video deleted successfully");
          fetchVideos();
        })
        .catch(err => {
          console.error(err);
          const serverMessage = err.response?.data?.message || err.message;
          toast.error('Delete failed: ' + serverMessage);
        });
    }
  };

  const handleUpload = (e) => {
    e.preventDefault();
    if (!formData.title || !file) {
      toast("Please provide a title and select a file.");
      return;
    }
    
    if (file.size > 30 * 1024 * 1024) {
      toast.error("File size cannot exceed 30MB.");
      return;
    }
    
    const validTypes = ['video/mp4', 'video/webm', 'video/ogg'];
    if (file.type && !validTypes.includes(file.type)) {
      toast.error("Invalid file format. Please upload MP4, WebM, or OGG videos.");
      return;
    }
    
    const data = new FormData();
    data.append('file', file);
    data.append('title', formData.title);
    data.append('subject', formData.subject || 'General');
    data.append('type', 'Video');

    axios.post(API_BASE_URL + '/api/files/upload', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    .then(res => {
      fetchVideos();
      setIsUploading(false);
      setFormData({ title: '', subject: '', difficulty: 'Medium' });
      setFile(null);
    })
    .catch(err => {
      console.error(err);
      const serverMessage = err.response?.data ? (typeof err.response.data === 'string' ? err.response.data : JSON.stringify(err.response.data)) : err.message;
      toast.error('Upload failed: ' + serverMessage);
    });
  };

  if (isUploading) {
    return (
      <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <h3 className="text-xl font-medium text-slate-200 mb-4">Upload Video</h3>
        <form onSubmit={handleUpload} className="space-y-4 max-w-xl">
          <div>
            <input type="file" accept="video/mp4,video/webm,video/ogg" onChange={e => {
              const selectedFile = e.target.files[0];
              if (selectedFile && selectedFile.size > 30 * 1024 * 1024) {
                toast.error("File size cannot exceed 30MB.");
                e.target.value = null;
                setFile(null);
              } else {
                setFile(selectedFile);
              }
            }} className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-500/10 file:text-blue-400 hover:file:bg-blue-500/20" required />
            <p className="text-xs text-slate-500 mt-2 ml-1">Max size: 30MB. Allowed formats: MP4, WebM, OGG</p>
          </div>
          <input type="text" placeholder="Video Title" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-slate-200" required />
          <input type="text" placeholder="Subject" value={formData.subject} onChange={e => setFormData({...formData, subject: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-slate-200" />
          <div className="flex gap-4 pt-4">
            <button type="button" onClick={() => setIsUploading(false)} className="px-4 py-2 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</button>
            <button type="submit" className="px-4 py-2 rounded-xl bg-blue-500 text-white hover:bg-blue-600">Upload</button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {videos.map(video => (
        <div key={video.id} className="group relative rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden hover:border-blue-500/50 transition-all duration-300">
          <div className={`aspect-video w-full bg-blue-500/20 border-b border-slate-800 relative flex items-center justify-center`}>
            <div className="absolute inset-0 bg-slate-950/20 group-hover:bg-transparent transition-colors duration-300"></div>
            <div className="flex gap-3 opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300">
              <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigator.clipboard.writeText(`${API_BASE_URL.startsWith('http') ? API_BASE_URL : window.location.origin + API_BASE_URL}/api/files/download/${video.id}`); toast.success("Link copied!"); }} className="w-12 h-12 rounded-full bg-slate-800/90 hover:bg-slate-700 text-white flex items-center justify-center shadow-xl shadow-slate-900/50" title="Copy Link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
              </button>
              <a href={`${API_BASE_URL}/api/files/download/${video.id}`} download className="w-12 h-12 rounded-full bg-blue-500/90 text-white flex items-center justify-center shadow-xl shadow-blue-500/30" title="Download">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
              </a>
              <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(video.id); }} className="w-12 h-12 rounded-full bg-rose-500/90 hover:bg-rose-600 text-white flex items-center justify-center shadow-xl shadow-rose-500/30" title="Delete Video">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
              </button>
            </div>
            <span className="absolute bottom-2 right-2 px-2 py-1 rounded-md bg-slate-950/80 text-xs font-medium text-slate-300 backdrop-blur-sm">
              {video.size}
            </span>
          </div>
          <div className="p-5">
            <div className="flex items-start justify-between mb-2">
              <span className="px-2.5 py-1 rounded-full bg-slate-800 text-xs font-medium text-blue-400 border border-slate-700">
                {video.subject}
              </span>
              <span className="text-xs text-slate-500 font-mono">v1.0</span>
            </div>
            <h3 className="text-lg font-semibold text-slate-200 group-hover:text-blue-400 transition-colors line-clamp-1">{video.title}</h3>
            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <div className="truncate">{video.originalFileName}</div>
            </div>
          </div>
        </div>
      ))}
      <button onClick={() => setIsUploading(true)} className="group rounded-2xl border-2 border-dashed border-slate-800 bg-slate-900/20 hover:bg-slate-800/40 hover:border-slate-600 flex flex-col items-center justify-center min-h-[280px] transition-all duration-300">
        <div className="w-14 h-14 rounded-full bg-slate-800 group-hover:bg-blue-500/20 text-slate-400 group-hover:text-blue-400 flex items-center justify-center mb-4 transition-colors">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
        </div>
        <span className="text-slate-400 group-hover:text-slate-200 font-medium">Upload Video</span>
      </button>
    </div>
  );
};

const StudyMaterials = () => {
  const [materials, setMaterials] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({ title: '', subject: '' });
  const [file, setFile] = useState(null);

  React.useEffect(() => { fetchMaterials(); }, []);

  const fetchMaterials = () => {
    axios.get(API_BASE_URL + '/api/files/type/PDF')
      .then(res => setMaterials(res.data))
      .catch(err => console.error("Error fetching materials:", err));
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this document?")) {
      axios.delete(`${API_BASE_URL}/api/files/${id}`)
        .then(() => {
          toast.success("Document deleted successfully");
          fetchMaterials();
        })
        .catch(err => {
          console.error(err);
          const serverMessage = err.response?.data?.message || err.message;
          toast.error('Delete failed: ' + serverMessage);
        });
    }
  };

  const handleUpload = (e) => {
    e.preventDefault();
    if (!formData.title || !file) {
      toast("Please provide a title and select a file.");
      return;
    }
    
    const data = new FormData();
    data.append('file', file);
    data.append('title', formData.title);
    data.append('subject', formData.subject || 'General');
    data.append('type', 'PDF');

    axios.post(API_BASE_URL + '/api/files/upload', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    .then(res => {
      fetchMaterials();
      setIsUploading(false);
      setFormData({ title: '', subject: '' });
      setFile(null);
    })
    .catch(err => {
      console.error(err);
      const serverMessage = err.response?.data ? (typeof err.response.data === 'string' ? err.response.data : JSON.stringify(err.response.data)) : err.message;
      toast.error('Upload failed: ' + serverMessage, { autoClose: false });
    });
  };

  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-slate-200">Recent Documents</h3>
        <button onClick={() => setIsUploading(true)} className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors shadow-lg shadow-blue-500/20 flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
          Upload PDF
        </button>
      </div>

      {isUploading && (
        <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6 mb-6 animate-in fade-in">
          <h3 className="text-xl font-medium text-slate-200 mb-4">Upload Study Material</h3>
          <form onSubmit={handleUpload} className="space-y-4 max-w-xl">
            <input type="file" accept=".pdf,.doc,.docx" onChange={e => setFile(e.target.files[0])} className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-500/10 file:text-blue-400 hover:file:bg-blue-500/20" required />
            <input type="text" placeholder="Document Title" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-slate-200" required />
            <input type="text" placeholder="Subject" value={formData.subject} onChange={e => setFormData({...formData, subject: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-slate-200" />
            <div className="flex gap-4 pt-4">
              <button type="button" onClick={() => setIsUploading(false)} className="px-4 py-2 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</button>
              <button type="submit" className="px-4 py-2 rounded-xl bg-blue-500 text-white hover:bg-blue-600">Upload</button>
            </div>
          </form>
        </div>
      )}
      
      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
        <div className="grid grid-cols-12 gap-4 p-4 border-b border-slate-800/60 bg-slate-900/60 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <div className="col-span-5">File Name</div>
          <div className="col-span-2">Subject</div>
          <div className="col-span-2">Size</div>
          <div className="col-span-2">Uploaded</div>
          <div className="col-span-1 text-right">Action</div>
        </div>
        
        <div className="divide-y divide-slate-800/60">
          {materials.map(doc => (
            <div key={doc.id} className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-slate-800/40 transition-colors group text-sm">
              <div className="col-span-5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-rose-500/10 text-rose-400 flex items-center justify-center shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                </div>
                <div className="flex flex-col">
                  <span className="font-medium text-slate-200 group-hover:text-blue-400 transition-colors">{doc.title}</span>
                  <span className="text-xs text-slate-500 truncate max-w-[200px]">{doc.originalFileName}</span>
                </div>
              </div>
              <div className="col-span-2 text-slate-400">
                <span className="inline-flex px-2 py-1 rounded bg-slate-800 text-xs text-slate-300">{doc.subject}</span>
              </div>
              <div className="col-span-2 text-slate-500">{doc.size}</div>
              <div className="col-span-2 text-slate-500">
                <div className="flex flex-col">
                  <span className="text-slate-300">{new Date(doc.uploadDate).toLocaleDateString()}</span>
                  <span className="text-xs">{doc.uploadedBy}</span>
                </div>
              </div>
              <div className="col-span-1 flex justify-end gap-2">
                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigator.clipboard.writeText(`${API_BASE_URL.startsWith('http') ? API_BASE_URL : window.location.origin + API_BASE_URL}/api/files/download/${doc.id}`); toast.success("Link copied!"); }} className="w-8 h-8 rounded-lg bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 flex items-center justify-center transition-colors" title="Copy Link">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                </button>
                <a href={`${API_BASE_URL}/api/files/download/${doc.id}`} download className="w-8 h-8 rounded-lg bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 flex items-center justify-center transition-colors" title="Download">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                </a>
                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(doc.id); }} className="w-8 h-8 rounded-lg bg-rose-500/10 text-rose-400 hover:text-white hover:bg-rose-500 flex items-center justify-center transition-colors" title="Delete">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const renderRichText = (raw) => {
  if (!raw) return { __html: '' };
  let html = String(raw);
  const cleanMath = (m) => m.replace(/&nbsp;/g, ' ').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').replace(/<[^>]+>/g, '');
  
  // Convert [IMAGE: url] markers produced by the Python parser to <img> tags
  html = html.replace(/\[IMAGE:\s*([^\]]+)\]/g, (match, url) => {
    const src = url.trim();
    return `<img src="${src}" alt="Question Image" style="display:inline-block; vertical-align:middle; margin:4px; max-width:100%; max-height:300px; border-radius:4px;" onerror="this.style.display='none'" />`;
  });

  try {
    // Check for KaTeX before rendering
    if (window.katex) {
      html = html.replace(/\$\$([\s\S]*?)\$\$/g, (match, math) => {
        try { return window.katex.renderToString(cleanMath(math), { displayMode: true, throwOnError: false }); } 
        catch (e) { return match; }
      });
      html = html.replace(/(?<!\$)\$([^$]+)\$(?!\$)/g, (match, math) => {
        try { return window.katex.renderToString(cleanMath(math), { displayMode: false, throwOnError: false }); } 
        catch (e) { return match; }
      });
    }
  } catch (err) {}
  // Force <img> tags to be inline if they are inside <p> or standalone
  html = html.replace(/<img /g, '<img style="display:inline-block; vertical-align:middle; margin:0 4px;" ');
  return { __html: html };
};


  const QuestionBanks = ({ setActiveTab }) => {
    const fileInputRef = useRef(null);
    const [questions, setQuestions] = useState([]);
    const [isCreating, setIsCreating] = useState(false);
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [editingData, setEditingData] = useState(null); // The question object currently being edited in modal
    const [filters, setFilters] = useState({ subject: '', chapter: '', difficulty: '', questionType: '', customTags: '' });
    const [chaptersList, setChaptersList] = useState([]);
    const [subjectsList, setSubjectsList] = useState([]);
    const [difficultiesList, setDifficultiesList] = useState([]);
    const [questionTypesList, setQuestionTypesList] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const qualityThreshold = 90;

  const [activeEditor, setActiveEditor] = useState(null); 
  const quillRefs = useRef({}); 

  const applyValueToEditorState = useCallback((editorId, newValue) => {
    if (editorId.startsWith('create_text')) {
      setFormData(prev => ({ ...prev, text: newValue }));
    }
    else if (editorId.startsWith('create_solution')) {
      setFormData(prev => ({ ...prev, solution: newValue }));
    }
    else if (editorId.startsWith('create_opt_')) {
      const idx = parseInt(editorId.split('_').pop(), 10);
      setFormData(prev => {
        const newOptions = [...prev.options];
        newOptions[idx] = newValue;
        return { ...prev, options: newOptions };
      });
    }
    else if (editorId.startsWith('edit_text_')) {
      setEditingData(prev => prev ? { ...prev, text: newValue } : prev);
    }
    else if (editorId.startsWith('edit_solution_')) {
      setEditingData(prev => prev ? { ...prev, solution: newValue } : prev);
    }
    else if (editorId.startsWith('edit_opt_')) {
      const idx = parseInt(editorId.split('_').pop(), 10);
      setEditingData(prev => {
        if (!prev) return prev;
        const newOptions = [...prev.options];
        newOptions[idx] = newValue;
        return { ...prev, options: newOptions };
      });
    }
  }, []);

  const insertSymbol = useCallback((symbol) => {
    if (!activeEditor) return;
    const el = quillRefs.current[activeEditor.id];
    if (!el) return;
    const editorId = activeEditor.id;

    if (el.isContentEditable) {
      el.focus();
      const selection = window.getSelection();
      let range = null;

      if (selection && selection.rangeCount > 0 && el.contains(selection.anchorNode)) {
        range = selection.getRangeAt(0);
      } else {
        range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
      }

      range.deleteContents();
      const textNode = document.createTextNode(symbol);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);

      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }

      applyValueToEditorState(editorId, el.innerHTML);
      return;
    }

    // Handle standard textarea/input
    if (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const text = el.value;
      const newValue = text.substring(0, start) + symbol + text.substring(end);
      applyValueToEditorState(editorId, newValue);

      // Restore focus and cursor
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start + symbol.length, start + symbol.length);
      }, 0);
    } 
    // Handle Quill fallback
    else if (el.getEditor) {
      const quill = el.getEditor();
      const range = quill.getSelection(true);
      quill.insertText(range.index, symbol);
      quill.setSelection(range.index + 1);
    }
  }, [activeEditor, applyValueToEditorState]);


  const startEditing = useCallback((q) => {
    setEditingData({ ...q });
  }, []);

  const handleEditOptionChange = useCallback((index, value) => {
    setEditingData(prev => {
      const newOptions = [...prev.options];
      newOptions[index] = value;
      return { ...prev, options: newOptions };
    });
  }, []);

  const toggleCorrectOption = (letter, isEdit = false) => {
    const setter = isEdit ? setEditingData : setFormData;
    setter(prev => {
      let current = String(prev.correctOption || '');
      if (current.includes(letter)) {
        current = current.replace(letter, '');
      } else {
        current += letter;
      }
      // Sort alphabetically
      const sorted = current.split('').sort().join('');
      return { ...prev, correctOption: sorted };
    });
  };

  const submitEdit = (e) => {
    if (!editingData.text.trim()) {
      toast("Please fill question content.");
      return;
    }
    const isInteger = editingData.questionType === 'Integer Type';
    const isMultiple = editingData.questionType === 'Multiple Correct';
    if (!isInteger && !isMultiple && editingData.options.some(opt => !String(opt).trim())) {
      toast("Please fill all options.");
      return;
    }
    if ((isInteger || isMultiple) && (!editingData.correctOption || !String(editingData.correctOption).trim())) {
      toast("Please enter the correct answer.");
      return;
    }

    axios.put(API_BASE_URL + '/api/questions-test/' + editingData.id, editingData)
      .then(() => {
        fetchQuestions();
        setEditingData(null);
        toast.success("Question updated!");
      })
      .catch(err => {
        console.error("Error updating question:", err);
        toast("Error updating database.");
      });
  };

  React.useEffect(() => {
    fetchQuestions(0);
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    axios
      .get(API_BASE_URL + "/api/questions-test/metadata/subjects")
      .then((res) => setSubjectsList(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Error fetching subjects:", err));

    axios
      .get(API_BASE_URL + "/api/questions-test/metadata/difficulties")
      .then((res) => setDifficultiesList(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Error fetching difficulties:", err));

    axios
      .get(API_BASE_URL + "/api/questions-test/metadata/question-types")
      .then((res) => setQuestionTypesList(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Error fetching question types:", err));
  }, []);

  // Fetch unique chapters when subject changes
  React.useEffect(() => {
    if (filters.subject) {
      axios.get(`${API_BASE_URL}/api/questions-test/metadata/chapters`, { params: { subject: filters.subject } })
        .then(res => setChaptersList(res.data))
        .catch(err => console.error("Error fetching chapters:", err));
    } else {
      setChaptersList([]);
      setFilters(prev => ({ ...prev, chapter: '' }));
    }
  }, [filters.subject]);


  const fetchQuestions = useCallback((page = currentPage) => {
    setIsLoading(true);
    axios.get(API_BASE_URL + '/api/questions-test', { params: { ...filters, page, size: 200 } })
      .then(res => {
        const mapped = res.data.map(q => {
          const isInteger = q.questionType === 'Integer Type';
          const isMultiple = q.questionType === 'Multiple Correct';
          return {
            ...q,
            options: [q.optionA, q.optionB, q.optionC, q.optionD],
            correctOptionIndex: (!isInteger && !isMultiple && q.correctOption && q.correctOption.length === 1) 
              ? q.correctOption.charCodeAt(0) - 65 
              : 0
          };
        });
        // Backend order is generally oldest first, let's reverse to show newest first
        setQuestions(mapped.reverse());
        setCurrentPage(page);
        setIsLoading(false);
      })
      .catch(err => {
        console.error("Error fetching questions:", err);
        setIsLoading(false);
      });
    
    // Fetch filtered count
    axios.get(API_BASE_URL + '/api/questions-test/count', { params: { ...filters } })
      .then(res => setTotalCount(res.data))
      .catch(err => console.error("Error fetching count:", err));
  }, [filters, currentPage]);


  const [formData, setFormData] = useState({
    text: '',
    options: ['', '', '', ''],
    correctOptionIndex: 0,
    subject: '',
    chapter: '',
    difficulty: 'Medium',
    questionType: 'Single Correct',
    correctOption: 'A',
    customTags: '',
    targetClass: '',
    solution: ''
  });

  const handleOptionChange = useCallback((index, value) => {
    setFormData(prev => {
      const newOptions = [...prev.options];
      newOptions[index] = value;
      return { ...prev, options: newOptions };
    });
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const isInteger = formData.questionType === 'Integer Type';
    const isMultiple = formData.questionType === 'Multiple Correct';
    if (!formData.text.trim()) {
      toast("Please enter question text.");
      return;
    }
    if (!isInteger && !isMultiple && formData.options.some(opt => !opt.trim())) {
      toast("Please fill all 4 options.");
      return;
    }
    if ((isInteger || isMultiple) && !formData.correctOption.trim()) {
      toast("Please enter the correct answer.");
      return;
    }

    const newQuestion = {
      ...formData,
      id: Date.now(),
      date: new Date().toISOString()
    };
    
    // Save to DB via bulk endpoint (even for a single question)
    axios.post(API_BASE_URL + '/api/questions-test/bulk', [newQuestion])
      .then(() => {
        fetchQuestions();
        setIsCreating(false);
        setFormData({
          text: '',
          options: ['', '', '', ''],
          correctOptionIndex: 0,
          subject: '',
          chapter: '',
          topic: '',
          difficulty: 'Medium',
          questionType: 'Multiple Choice Single Correct',
          correctOption: '',
          customTags: '',
          solution: ''
        });
  })
  .catch(err => {
    console.error("Error saving manual question:", err);
    toast("Error saving. DB might be down.");
  });
};

  const removeQuestion = useCallback((id) => {
    if (window.confirm("Are you sure you want to delete this question?")) {
      axios.delete(`${API_BASE_URL}/api/questions-test/${id}`)
        .then(() => {
          setQuestions(prev => prev.filter(q => q.id !== id));
          toast.success("Question deleted successfully");
        })
        .catch(err => {
          console.error("Delete error:", err);
          toast.error("Failed to delete question");
        });
    }
  }, []);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Close any open import modals now that a file is selected
    setIsBulkModalOpen(false);

    if (file.name.toLowerCase().endsWith('.docx')) {
      const formData = new FormData();
      formData.append('file', file);

      const uploadToast = toast.loading('🔍 Uploading DOCX — running quality-scored parser waterfall...');

      axios.post(`${API_BASE_URL}/api/docx/upload?threshold=${qualityThreshold}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000, // 10 min — AI parser generation can take a while
      })
      .then(res => {
        let data = {};
        try { data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data; } catch (_) {}

        const score   = data.score          != null ? data.score.toFixed(1)        : '?';
        const qCount  = data.totalQuestions != null ? data.totalQuestions           : '?';
        const parser  = data.parserUsed     || 'unknown';
        const below   = data.belowThreshold === true;

        if (below) {
          // Parsed but quality was still below threshold even after AI fallback
          toast.update(uploadToast, {
            render: (
              <div>
                <div className="font-semibold text-yellow-300">⚠️ Parsed with best-effort quality</div>
                <div className="text-xs mt-1 text-slate-300">
                  {qCount} questions imported · Score: <b>{score}%</b> (below {qualityThreshold}%)
                </div>
                <div className="text-xs text-slate-400 mt-0.5">Parser: {parser}</div>
                <div className="text-xs text-slate-400">An improved parser was generated for next upload.</div>
              </div>
            ),
            type: 'warning', isLoading: false, autoClose: 8000,
          });
        } else {
          toast.update(uploadToast, {
            render: (
              <div>
                <div className="font-semibold text-emerald-300">✅ Questions imported successfully</div>
                <div className="text-xs mt-1 text-slate-300">
                  {qCount} questions · Quality score: <b>{score}%</b>
                </div>
                <div className="text-xs text-slate-400 mt-0.5">Parser: {parser}</div>
              </div>
            ),
            type: 'success', isLoading: false, autoClose: 5000,
          });
        }
        fetchQuestions();
      })
      .catch(err => {
        console.error('DOCX Upload Error:', err);
        let msg = 'Error parsing DOCX.';
        try {
          const data = typeof err.response?.data === 'string'
            ? JSON.parse(err.response.data)
            : err.response?.data;
          if (data?.message) msg = data.message;
        } catch (_) {}
        toast.update(uploadToast, {
          render: `❌ ${msg} Check that Python + dependencies are installed on the server.`,
          type: 'error', isLoading: false, autoClose: 7000,
        });
      });
      
      e.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);
        
        const newQuestions = data.map((row, idx) => ({
          id: Date.now() + idx,
          text: row['Question'] || '',
          options: [
            row['Option A'] || '',
            row['Option B'] || '',
            row['Option C'] || '',
            row['Option D'] || ''
          ],
          correctOption: String(row['Correct Option'] || ''),
          correctOptionIndex: (row['Correct Option'] && String(row['Correct Option']).trim().length === 1 && /^[A-D]$/i.test(String(row['Correct Option']).trim())) 
            ? String(row['Correct Option']).trim().toUpperCase().charCodeAt(0) - 65 
            : 0,
          subject: row['Subject'] || 'N/A',
          chapter: row['File Name'] || row['Chapter'] || '',
          topic: row['Topic'] || '',
          difficulty: row['Difficulty'] || 'Medium',
          customTags: row['Status'] || row['Custom Tags'] || '',
          solution: row['Solution'] || '',
          questionType: row['Question Type'] || 'Single Correct',
          targetClass: row['Target Class'] || '',
          date: new Date().toISOString()
        }));

        setQuestions(prev => [...newQuestions, ...prev]);
        
        axios.post(API_BASE_URL + '/api/questions-test/bulk', newQuestions)
          .then(res => {
            toast(`Successfully imported and saved ${newQuestions.length} questions to the database!`);
            fetchQuestions();
          })
          .catch(err => {
            console.error('API Error:', err);
            toast(`Questions loaded in UI, but failed to save to database. Error: ${err.message}`);
          });
      } catch (error) {
        console.error(error);
        toast('Error parsing the Excel file. Please ensure it follows the sample format.');
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = null; // reset
  };


  if (isCreating) {
    return (
      <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center justify-between mb-6 border-b border-slate-800 pb-4">
          <h3 className="text-xl font-medium text-slate-200">Create New MCQ</h3>
          <button 
            onClick={() => setIsCreating(false)}
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Cancel
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Question Text */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">Question Text</label>
            <div className="flex flex-col">
              <SymbolPicker onSelect={insertSymbol} />
              <div className="bg-slate-950/50 border border-slate-800 rounded-b-xl overflow-hidden [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-slate-800 [&_.ql-toolbar]:bg-slate-900/60 [&_.ql-container]:border-0 [&_.ql-editor]:min-h-[100px] [&_.ql-editor]:text-slate-200 [&_.ql-picker]:text-slate-300 [&_.ql-stroke]:stroke-slate-400 [&_.ql-fill]:fill-slate-400">
                <textarea 
                  ref={(el) => { if (el) quillRefs.current['create_text'] = el; }}
                  onFocus={() => setActiveEditor({ id: 'create_text' })}
                  value={formData.text}
                  onChange={(e) => setFormData({...formData, text: e.target.value})}
                  className="w-full bg-transparent p-4 min-h-[120px] text-slate-200 outline-none resize-y"
                  placeholder="Enter question content (HTML supported)..."
                />
              </div>
            </div>
          </div>

          {/* Tagging Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pt-4 border-t border-slate-800">
            <div className="space-y-2">
              <select 
                value={formData.questionType}
                onChange={(e) => setFormData({...formData, questionType: e.target.value, correctOption: ''})}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
              >
                <option value="Single Correct">Single Correct</option>
                <option value="Multiple Correct">Multiple Correct</option>
                <option value="Integer Type">Integer Type</option>
                <option value="Assertion Reason">Assertion Reason</option>
              </select>
            </div>
          </div>

          {/* Options Section */}
          <div className="space-y-4 pt-4 border-t border-slate-800">
            {formData.questionType === 'Integer Type' ? (
              <div className="space-y-2 max-w-xs">
                <label className="text-sm font-medium text-slate-300">Integer Answer</label>
                <input 
                  type="text"
                  maxLength={10}
                  value={formData.correctOption}
                  onChange={(e) => setFormData({...formData, correctOption: e.target.value})}
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-xl p-4 text-emerald-400 font-mono font-bold text-lg focus:border-emerald-500 outline-none transition-all placeholder:text-slate-700"
                  placeholder="Enter integer..."
                />
                <p className="text-[10px] text-slate-500 italic">This will be stored as the correct answer (max 10 chars).</p>
              </div>
            ) : formData.questionType === 'Multiple Correct' ? (
              <>
                <label className="text-sm font-medium text-slate-300">Options & Correct Answers (Multiple Selection)</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formData.options.map((opt, idx) => (
                    <div key={idx} className="flex flex-col bg-slate-950/50 border border-slate-800 rounded-xl overflow-hidden focus-within:border-blue-500 transition-colors">
                      <div className="flex items-center gap-3 p-2 border-b border-slate-800">
                        <input 
                          type="checkbox" 
                          checked={String(formData.correctOption || '').includes(String.fromCharCode(65 + idx))}
                          onChange={() => toggleCorrectOption(String.fromCharCode(65 + idx))}
                          className="w-4 h-4 ml-2 accent-emerald-500 cursor-pointer"
                        />
                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Option {String.fromCharCode(65 + idx)}</span>
                      </div>
                      <SymbolPicker onSelect={insertSymbol} />
                      <div className="flex-1 min-h-[60px]">
                        <textarea 
                          ref={(el) => { if (el) quillRefs.current[`create_opt_${idx}`] = el; }}
                          onFocus={() => setActiveEditor({ id: `create_opt_${idx}` })}
                          value={opt}
                          onChange={(e) => handleOptionChange(idx, e.target.value)}
                          className="w-full bg-transparent p-3 text-sm text-slate-200 outline-none resize-none min-h-[80px]"
                          placeholder={`Type option content...`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 italic">Multi-correct answer: {formData.correctOption || '(none selected)'}</p>
              </>
            ) : (
              <>
                <label className="text-sm font-medium text-slate-300">Options & Correct Answer</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formData.options.map((opt, idx) => (
                    <div key={idx} className="flex flex-col bg-slate-950/50 border border-slate-800 rounded-xl overflow-hidden focus-within:border-blue-500 transition-colors">
                      <div className="flex items-center gap-3 p-2 border-b border-slate-800">
                        <input 
                          type="radio" 
                          name="correctOption" 
                          checked={formData.correctOptionIndex === idx}
                          onChange={() => setFormData({...formData, correctOptionIndex: idx, correctOption: String.fromCharCode(65 + idx)})}
                          className="w-4 h-4 ml-2 accent-blue-500"
                        />
                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Option {String.fromCharCode(65 + idx)}</span>
                      </div>
                      <SymbolPicker onSelect={insertSymbol} />
                      <div className="flex-1 min-h-[60px]">
                        <textarea 
                          ref={(el) => { if (el) quillRefs.current[`create_opt_${idx}`] = el; }}
                          onFocus={() => setActiveEditor({ id: `create_opt_${idx}` })}
                          value={opt}
                          onChange={(e) => handleOptionChange(idx, e.target.value)}
                          className="w-full bg-transparent p-3 text-sm text-slate-200 outline-none resize-none min-h-[80px]"
                          placeholder={`Type option content...`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500">Select the radio button next to the correct option.</p>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pt-4 border-t border-slate-800">
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-400">Subject</label>
              <input 
                type="text" 
                value={formData.subject}
                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                placeholder="e.g. Physics"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-400">Chapter</label>
              <input 
                type="text" 
                value={formData.chapter}
                onChange={(e) => setFormData({...formData, chapter: e.target.value})}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                placeholder="e.g. Thermodynamics"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-400">Topic</label>
              <input 
                type="text" 
                value={formData.topic}
                onChange={(e) => setFormData({...formData, topic: e.target.value})}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                placeholder="e.g. Entropy"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-400">Target Class / Grade</label>
              <input 
                type="text" 
                value={formData.targetClass}
                onChange={(e) => setFormData({...formData, targetClass: e.target.value})}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                placeholder="e.g. Class 12, NEET Batch"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-400">Difficulty</label>
              <select 
                value={formData.difficulty}
                onChange={(e) => setFormData({...formData, difficulty: e.target.value})}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
              >
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </select>
            </div>
            <div className="space-y-2 xl:col-span-2">
              <label className="text-xs font-medium text-slate-400">Custom Tags (comma separated)</label>
              <input 
                type="text" 
                value={formData.customTags}
                onChange={(e) => setFormData({...formData, customTags: e.target.value})}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                placeholder="e.g. previous-year, important"
              />
            </div>
            {/* Solution Editor */}
            <div className="space-y-2 xl:col-span-3">
              <label className="text-sm font-medium text-slate-300">Detailed Solution</label>
              <div className="flex flex-col">
                <SymbolPicker onSelect={insertSymbol} />
                <div className="bg-slate-950/50 border border-slate-800 rounded-b-xl overflow-hidden [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-slate-800 [&_.ql-toolbar]:bg-slate-900/60 [&_.ql-container]:border-0 [&_.ql-editor]:min-h-[100px] [&_.ql-editor]:text-slate-200 [&_.ql-picker]:text-slate-300 [&_.ql-stroke]:stroke-slate-400 [&_.ql-fill]:fill-slate-400">
                  <textarea 
                    ref={(el) => { if (el) quillRefs.current['create_solution'] = el; }}
                    onFocus={() => setActiveEditor({ id: 'create_solution' })}
                    value={formData.solution}
                    onChange={(e) => setFormData({...formData, solution: e.target.value})}
                    className="w-full bg-transparent p-4 min-h-[100px] text-slate-200 outline-none resize-y"
                    placeholder="Provide a step-by-step solution..."
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-6 border-t border-slate-800 gap-4">
            <button 
              type="button"
              onClick={() => setIsCreating(false)}
              className="px-6 py-2.5 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="px-6 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-medium shadow-lg shadow-blue-500/20 transition-colors"
            >
              Save Question
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-medium text-slate-200 flex items-center gap-2">
            Question Bank
            <span className="text-[10px] bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded-full border border-blue-500/20 uppercase tracking-wider">
              {isLoading ? 'Syncing...' : `${totalCount.toLocaleString()} Total`}
            </span>
          </h3>
          <p className="text-sm text-slate-500 mt-1">Manage MCQs and adaptative question sets.</p>
        </div>
        <div className="flex gap-3">
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileUpload} 
            accept=".xlsx, .xls, .csv, .docx" 
            className="hidden" 
          />
          <button 
            onClick={() => setIsBulkModalOpen(true)}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium transition-colors border border-slate-700 flex items-center gap-2"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
            Bulk Upload
          </button>
          <button 
            onClick={() => setIsCreating(true)}
            className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors shadow-lg shadow-blue-500/20 flex items-center gap-2"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            Add MCQ
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center p-4 bg-slate-900/40 border border-slate-800 rounded-2xl gap-4">
        <div className="flex items-center gap-2 text-slate-400">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
          <span className="text-sm font-medium">Filters:</span>
        </div>
        <div className="flex flex-wrap gap-3 flex-1">
          <select
            value={filters.subject}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                subject: e.target.value,
                chapter: "",
              }))
            }
            className="bg-slate-950/60 border border-slate-800 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500 w-[220px] max-w-[220px]"
          >
            <option value="">All Subjects</option>
            {subjectsList.map((subject) => (
              <option key={subject} value={subject}>
                {subject}
              </option>
            ))}
          </select>

          {filters.subject && (
            <select
              value={filters.chapter}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, chapter: e.target.value }))
              }
              className="bg-slate-950/60 border border-slate-800 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500 w-[220px] max-w-[220px] animate-in fade-in slide-in-from-left-2"
            >
              <option value="">All Chapters</option>
              {chaptersList.map(ch => (
                <option key={ch} value={ch}>{ch}</option>
              ))}
            </select>
          )}

          <select
            value={filters.difficulty}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, difficulty: e.target.value }))
            }
            className="bg-slate-950/60 border border-slate-800 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500 w-[190px] max-w-[190px]"
          >
            <option value="">All Difficulties</option>
            {difficultiesList.map((difficulty) => (
              <option key={difficulty} value={difficulty}>
                {difficulty}
              </option>
            ))}
          </select>

          <select
            value={filters.questionType}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, questionType: e.target.value }))
            }
            className="bg-slate-950/60 border border-slate-800 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500 w-[220px] max-w-[220px]"
          >
            <option value="">All Question Types</option>
            {questionTypesList.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Custom Tag (e.g. important)"
            value={filters.customTags}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, customTags: e.target.value }))
            }
            onKeyDown={(e) => e.key === "Enter" && fetchQuestions()}
            className="bg-slate-950/60 border border-slate-800 rounded-xl px-4 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500 flex-1 min-w-[200px]"
          />
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => fetchQuestions(0)} 
            disabled={isLoading}
            className="px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/50 text-white text-sm font-medium transition-colors shadow-lg shadow-blue-500/20 flex items-center gap-2"
          >
            {isLoading && <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>}
            {isLoading ? 'Loading...' : 'Show Questions'}
          </button>
          { (filters.subject || filters.chapter || filters.difficulty || filters.questionType || filters.customTags) && (
            <button onClick={() => {
              setFilters({subject: '', chapter: '', difficulty: '', questionType: '', customTags: ''});

              // Give state a millisecond to update, then fetch all
              setTimeout(() => {
                axios.get(API_BASE_URL + '/api/questions-test', { params: { subject: '', chapter: '', difficulty: '', questionType: '', customTags: '', page: 0, size: 200 } })
                  .then(res => {
                    const mapped = res.data.map(q => ({
                      ...q,
                      options: [q.optionA, q.optionB, q.optionC, q.optionD],
                      correctOptionIndex: q.correctOption ? q.correctOption.charCodeAt(0) - 65 : 0
                    }));
                    setQuestions(mapped.reverse());
                    setCurrentPage(0);
                  })
                  .catch(console.error);
              }, 0);
            }} className="text-xs text-rose-400 hover:text-rose-300 px-3 py-2 rounded-xl border border-rose-500/20 bg-rose-500/10 transition-colors">
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between px-2">
        <div className="text-sm text-slate-500">
          Showing <span className="text-slate-300 font-bold">{questions.length}</span> questions of <span className="text-slate-300 font-bold">{totalCount.toLocaleString()}</span> 
          {currentPage > 0 && <span> (Page {currentPage + 1})</span>}
        </div>
        <div className="flex items-center gap-3">
          <button 
            disabled={currentPage === 0}
            onClick={() => fetchQuestions(currentPage - 1)}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-slate-200 text-xs font-bold border border-slate-700 transition-all flex items-center gap-2"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            Previous 200
          </button>
          <button 
            disabled={questions.length < 200}
            onClick={() => fetchQuestions(currentPage + 1)}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-slate-200 text-xs font-bold border border-slate-700 transition-all flex items-center gap-2"
          >
            Next 200
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
          </button>
        </div>
      </div>

      <div className="relative min-h-[400px]">
        {isLoading && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-slate-950/40 backdrop-blur-[2px] rounded-3xl border border-slate-800/50">
            <div className="w-10 h-10 border-4 border-blue-500/10 border-t-blue-500 rounded-full animate-spin mb-4"></div>
            <p className="text-slate-400 text-sm font-medium animate-pulse">Syncing with Question Vault...</p>
          </div>
        )}

        {questions.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] border-2 border-dashed border-slate-800 rounded-3xl bg-slate-900/20">
            <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center text-slate-500 mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
            </div>
            <p className="text-slate-400 font-medium tracking-wide">No questions found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {questions.map((q) => (
              <QuestionItem 
                key={q.id} 
                q={q} 
                onEdit={startEditing} 
                onDelete={removeQuestion} 
                renderRichText={renderRichText} 
              />
            ))}
          </div>
        )}
      </div>

      {/* Edit Question Modal */}
      {editingData && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md" onClick={() => setEditingData(null)}></div>
          <div className="relative bg-slate-900 border border-slate-700 rounded-3xl p-6 md:p-8 w-full max-w-5xl max-h-[90vh] overflow-y-auto shadow-2xl animate-in zoom-in-95 duration-200 custom-scrollbar">
            <div className="flex items-center justify-between mb-6 border-b border-slate-800/60 pb-4">
              <h3 className="text-2xl font-bold text-white">Edit Question</h3>
              <button onClick={() => setEditingData(null)} className="text-slate-500 hover:text-white transition-colors">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>

            <form onSubmit={submitEdit} className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-400">Question Content</label>
                <div className="flex flex-col">
                  <SymbolPicker onSelect={insertSymbol} />
                  <div className="bg-slate-950/50 border border-slate-800 rounded-b-xl overflow-hidden [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-slate-800 [&_.ql-toolbar]:bg-slate-900/60 [&_.ql-container]:border-0 [&_.ql-editor]:min-h-[120px] [&_.ql-editor]:text-slate-200 [&_.ql-picker]:text-slate-300 [&_.ql-stroke]:stroke-slate-400 [&_.ql-fill]:fill-slate-400">
                    <HtmlEditor
                      editorId={`edit_text_${editingData.id}`}
                      registerRef={(el) => { if (el) quillRefs.current[`edit_text_${editingData.id}`] = el; }}
                      onFocus={() => setActiveEditor({ id: `edit_text_${editingData.id}` })}
                      value={editingData.text}
                      onChange={(value) => setEditingData({...editingData, text: value})}
                      placeholder="Edit question content..."
                      minHeightClass="min-h-[150px]"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-slate-800">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase ml-1">Question Type</label>
                  <select 
                    value={editingData.questionType}
                    onChange={(e) => setEditingData({...editingData, questionType: e.target.value, correctOption: ''})}
                    className="w-full max-w-xs bg-slate-950/50 border border-slate-800 rounded-xl p-3 text-sm text-slate-200 focus:border-blue-500 outline-none transition-colors"
                  >
                    <option value="Single Correct">Single Correct</option>
                    <option value="Multiple Correct">Multiple Correct</option>
                    <option value="Integer Type">Integer Type</option>
                    <option value="Assertion Reason">Assertion Reason</option>
                  </select>
                </div>

                {editingData.questionType === 'Integer Type' ? (
                  <div className="space-y-2 max-w-xs pt-2">
                    <label className="text-sm font-semibold text-slate-400">Integer Answer</label>
                    <input 
                      type="text"
                      maxLength={10}
                      value={editingData.correctOption}
                      onChange={(e) => setEditingData({...editingData, correctOption: e.target.value})}
                      className="w-full bg-slate-950/50 border border-slate-800 rounded-xl p-4 text-emerald-400 font-mono font-bold text-lg focus:border-emerald-500 outline-none transition-all"
                      placeholder="Enter integer..."
                    />
                    <p className="text-[10px] text-slate-500 italic">Correct answer for this integer question.</p>
                  </div>
                ) : editingData.questionType === 'Multiple Correct' ? (
                  <>
                    <label className="text-sm font-semibold text-slate-400">Options & Correct Answers (Multiple)</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {editingData.options.map((opt, idx) => (
                        <div key={idx} className="flex flex-col bg-slate-950/50 border border-slate-800 rounded-xl overflow-hidden focus-within:border-blue-500 transition-all">
                          <div className="flex items-center gap-3 p-2 border-b border-slate-800 bg-slate-900/40">
                            <input 
                              type="checkbox" 
                              checked={String(editingData.correctOption || '').includes(String.fromCharCode(65 + idx))}
                              onChange={() => toggleCorrectOption(String.fromCharCode(65 + idx), true)}
                              className="w-4 h-4 ml-2 accent-emerald-500 cursor-pointer"
                            />
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Option {String.fromCharCode(65 + idx)}</span>
                          </div>
                          <SymbolPicker onSelect={insertSymbol} />
                          <div className="flex-1 min-h-[60px]">
                            <HtmlEditor
                              editorId={`edit_opt_${editingData.id}_${idx}`}
                              registerRef={(el) => { if (el) quillRefs.current[`edit_opt_${editingData.id}_${idx}`] = el; }}
                              onFocus={() => setActiveEditor({ id: `edit_opt_${editingData.id}_${idx}` })}
                              value={opt}
                              onChange={(value) => handleEditOptionChange(idx, value)}
                              placeholder={`Type option content...`}
                              minHeightClass="min-h-[80px]"
                              paddingClass="p-3"
                              textClassName="text-sm"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {editingData.options.map((opt, idx) => (
                      <div key={idx} className="flex flex-col bg-slate-950/50 border border-slate-800 rounded-xl overflow-hidden focus-within:border-blue-500 transition-all">
                        <div className="flex items-center gap-3 p-2 border-b border-slate-800 bg-slate-900/40">
                          <input 
                            type="radio" 
                            name="modalCorrectOption" 
                            checked={editingData.correctOptionIndex === idx} 
                            onChange={() => setEditingData({...editingData, correctOptionIndex: idx, correctOption: String.fromCharCode(65 + idx)})} 
                            className="w-4 h-4 ml-2 accent-blue-500 cursor-pointer" 
                          />
                          <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Option {String.fromCharCode(65 + idx)}</span>
                        </div>
                        <SymbolPicker onSelect={insertSymbol} />
                        <div className="flex-1 min-h-[60px]">
                          <HtmlEditor
                            editorId={`edit_opt_${editingData.id}_${idx}`}
                            registerRef={(el) => { if (el) quillRefs.current[`edit_opt_${editingData.id}_${idx}`] = el; }}
                            onFocus={() => setActiveEditor({ id: `edit_opt_${editingData.id}_${idx}` })}
                            value={opt}
                            onChange={(value) => handleEditOptionChange(idx, value)}
                            placeholder={`Type option content...`}
                            minHeightClass="min-h-[80px]"
                            paddingClass="p-3"
                            textClassName="text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-slate-800/60">
                <div className="space-y-1.5">
                  <span className="text-[10px] font-bold text-slate-500 uppercase ml-1">Subject</span>
                  <input type="text" placeholder="Subject" value={editingData.subject} onChange={e => setEditingData({...editingData, subject: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:border-blue-500 outline-none transition-colors" />
                </div>
                <div className="space-y-1.5">
                  <span className="text-[10px] font-bold text-slate-500 uppercase ml-1">Chapter</span>
                  <input type="text" placeholder="Chapter" value={editingData.chapter} onChange={e => setEditingData({...editingData, chapter: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:border-blue-500 outline-none transition-colors" />
                </div>
                <div className="space-y-1.5">
                  <span className="text-[10px] font-bold text-slate-500 uppercase ml-1">Topic</span>
                  <input type="text" placeholder="Topic" value={editingData.topic} onChange={e => setEditingData({...editingData, topic: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:border-blue-500 outline-none transition-colors" />
                </div>
                <div className="space-y-1.5">
                  <span className="text-[10px] font-bold text-slate-500 uppercase ml-1">Target Class</span>
                  <input type="text" placeholder="Class / Grade" value={editingData.targetClass} onChange={e => setEditingData({...editingData, targetClass: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:border-blue-500 outline-none transition-colors" />
                </div>
                <div className="space-y-1.5">
                  <span className="text-[10px] font-bold text-slate-500 uppercase ml-1">Difficulty</span>
                  <select value={editingData.difficulty} onChange={e => setEditingData({...editingData, difficulty: e.target.value})} className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm text-slate-200 focus:border-blue-500 outline-none transition-colors">
                    <option value="Easy">Easy</option><option value="Medium">Medium</option><option value="Hard">Hard</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-400">Step-by-Step Solution</label>
                <div className="flex flex-col">
                  <SymbolPicker onSelect={insertSymbol} />
                  <div className="bg-slate-950/50 border border-slate-800 rounded-b-xl overflow-hidden [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-slate-800 [&_.ql-toolbar]:bg-slate-900/60 [&_.ql-container]:border-0 [&_.ql-editor]:min-h-[120px] [&_.ql-editor]:text-slate-200 [&_.ql-picker]:text-slate-300 [&_.ql-stroke]:stroke-slate-400 [&_.ql-fill]:fill-slate-400">
                    <HtmlEditor
                      editorId={`edit_solution_${editingData.id}`}
                      registerRef={(el) => { if (el) quillRefs.current[`edit_solution_${editingData.id}`] = el; }}
                      onFocus={() => setActiveEditor({ id: `edit_solution_${editingData.id}` })}
                      value={editingData.solution || ''}
                      onChange={(value) => setEditingData({...editingData, solution: value})}
                      placeholder="Add or edit the solution..."
                      minHeightClass="min-h-[150px]"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-6 gap-4 border-t border-slate-800/60">
                <button type="button" onClick={() => setEditingData(null)} className="px-6 py-2.5 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm font-medium transition-colors">Cancel</button>
                <button type="submit" className="px-8 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm font-bold shadow-xl shadow-blue-500/20 transition-all active:scale-[0.98]">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {isBulkModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 text-left">
          <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={() => setIsBulkModalOpen(false)}></div>
          <div className="relative bg-slate-900 border border-slate-800 rounded-3xl p-8 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-200">
            <button 
              onClick={() => setIsBulkModalOpen(false)}
              className="absolute top-4 right-4 text-slate-500 hover:text-slate-300 transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-blue-500/20 text-blue-400 flex items-center justify-center mx-auto mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
              </div>
              <h3 className="text-2xl font-bold text-slate-100">Bulk Upload MCQ</h3>
              <p className="text-slate-400 mt-2">Upload your questions using our Excel template for quick importing.</p>
            </div>
            
            <div className="space-y-6">
              <div className="p-4 rounded-2xl bg-slate-950/50 border border-slate-800">
                <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">Instructions</h4>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-1">•</span> Ensure all columns match the sample format.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-1">•</span> Format correct answers as 'A', 'B', 'C', or 'D'.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-1">•</span> Use <code>$$ math $$</code> for mathematical formulas.
                  </li>
                </ul>
              </div>

              <div className="flex flex-col gap-3">
                <a 
                  href="/sample_mcqs.xlsx" 
                  download 
                  className="w-full py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium flex items-center justify-center gap-2 transition-all border border-slate-700 hover:border-slate-500 group"
                >
                  <svg className="text-green-500 group-hover:scale-110 transition-transform" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                  Download Sample Excel
                </a>
                
                <button 
                  onClick={() => {
                    fileInputRef.current.click();
                  }}
                  className="w-full py-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold flex items-center justify-center gap-3 transition-all shadow-xl shadow-blue-500/20 active:scale-[0.98]"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
                  Select Excel File to Upload
                </button>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-slate-800"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-slate-900 px-2 text-slate-500 font-bold tracking-widest">OR</span>
                  </div>
                </div>

                <button 
                  onClick={() => {
                    setIsBulkModalOpen(false);
                    setActiveTab('import_docx');
                  }}
                  className="w-full py-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold flex items-center justify-center gap-3 transition-all border border-slate-700 hover:border-slate-600 active:scale-[0.98]"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
                  Import from DOCX
                </button>
                <p className="text-[10px] text-slate-500 text-center italic">
                  Note: DOCX parsing auto-selects the best parser and generates a new one via AI if quality is below 90%.
                </p>
              </div>
            </div>
            
            <p className="text-[10px] text-center text-slate-600 mt-6 uppercase tracking-[0.2em] font-bold">Supported formats: .xlsx, .xls, .csv</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// WhatsApp Cloud API (Meta) — TEST credentials (Pen Sense app)
// Read from REACT_APP_* env vars (set in pen/.env.local for dev; injected via
// GitHub Secrets in CI). Caveat: REACT_APP_* values are baked into the JS
// bundle at build time — they remain visible to anyone with DevTools. Move
// these calls to the Spring Boot backend before exposing to real users.
// Uses a fresh axios instance so the global app interceptor in App.jsx does
// NOT overwrite the Bearer token with the user's JWT.
// ---------------------------------------------------------------------------
const waAxios = axios.create();
const WA_ACCESS_TOKEN = process.env.REACT_APP_WA_ACCESS_TOKEN || '';
const WA_PHONE_NUMBER_ID = process.env.REACT_APP_WA_PHONE_NUMBER_ID || '';
const WA_GRAPH_VERSION = 'v21.0';
const WA_DEFAULT_TEMPLATE = 'study_time';
const WA_DEFAULT_LANG = 'en_US';

// study_time template (uses Graph v22.0 per the working Python script)
const WA_STUDY_GRAPH_VERSION = 'v22.0';
const WA_STUDY_TEMPLATE = 'study_time';
const WA_STUDY_LANG = 'en';
const WA_STUDY_TEST_TO = '919548550009';
const WA_STUDY_PARAMS = {
  mins3: '20'          // {{7}}
};

const ImportDocx = () => {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [qualityThreshold, setQualityThreshold] = useState(90);
  const [result, setResult] = useState(null);

  const handleDocxUpload = async () => {
    if (!file) {
      toast.error("Please select a DOCX file first.");
      return;
    }

    setIsUploading(true);
    setResult(null);
    const formData = new FormData();
    formData.append('file', file);

    const uploadToast = toast.loading('🔍 Uploading DOCX — running quality-scored parser waterfall...');

    try {
      const res = await axios.post(`${API_BASE_URL}/api/docx/upload?threshold=${qualityThreshold}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
      });
      
      let data = res.data;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch(e) { 
          console.error("Parse error:", e);
        }
      }

      setResult(data);
      const score = data.score != null ? data.score.toFixed(1) : '?';
      const qCount = data.totalQuestions != null ? data.totalQuestions : '?';

      if (data.belowThreshold) {
        toast.update(uploadToast, {
          render: `⚠️ Best-effort quality: ${qCount} Qs imported (Score: ${score}%)`,
          type: 'warning', isLoading: false, autoClose: 5000,
        });
      } else {
        toast.update(uploadToast, {
          render: `✅ Questions imported: ${qCount} (Score: ${score}%)`,
          type: 'success', isLoading: false, autoClose: 5000,
        });
      }
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || "Upload failed. Check server logs.";
      toast.update(uploadToast, {
        render: `❌ ${errorMessage}`,
        type: 'error', isLoading: false, autoClose: 8000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-xl font-bold text-white">DOCX Question Importer</h2>
          <p className="text-sm text-slate-500">Upload a structured DOCX exam paper. Our AI-powered waterfall ensures high-quality parsing.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-6">
          <div className="p-6 rounded-3xl border border-slate-800 bg-slate-900/40 hover:border-blue-500/30 transition-all group relative">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 text-blue-400 flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
              </div>
              <div>
                <h3 className="font-bold text-slate-200">Exam Paper</h3>
                <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">DOCX Format Only</p>
              </div>
            </div>
            <input 
              type="file" 
              accept=".docx"
              onChange={(e) => setFile(e.target.files[0])}
              className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-slate-800 file:text-slate-300 hover:file:bg-slate-700 cursor-pointer"
            />
            {file && <p className="mt-2 text-[10px] text-emerald-400 font-medium">✓ {file.name}</p>}
          </div>

          <div className="p-6 rounded-3xl border border-slate-800 bg-slate-900/40 hover:border-emerald-500/30 transition-all group">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><path d="m9 12 2 2 4-4"></path></svg>
                </div>
                <div>
                  <h3 className="font-bold text-slate-200">Quality Control</h3>
                  <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Waterfall Threshold</p>
                </div>
              </div>
              <span className="text-xl font-bold text-emerald-400">{qualityThreshold}%</span>
            </div>
            <input
              type="range"
              min="50" max="100" step="1"
              value={qualityThreshold}
              onChange={e => setQualityThreshold(Number(e.target.value))}
              className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-emerald-500"
            />
            <p className="mt-3 text-[10px] text-slate-500 italic leading-relaxed">
              If quality scores below this, the system automatically attempts to generate an improved AI parser.
            </p>
          </div>

          <button 
            onClick={handleDocxUpload}
            disabled={isUploading || !file}
            className={`w-full py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-lg shadow-xl shadow-blue-500/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-3 ${isUploading || !file ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {isUploading ? (
              <>
                <div className="w-5 h-5 border-3 border-white/20 border-t-white rounded-full animate-spin"></div>
                Orchestrating...
              </>
            ) : (
              <>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                Start Smart Import
              </>
            )}
          </button>
        </div>

        <div className="lg:col-span-2">
          <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-8 min-h-[500px] relative overflow-hidden flex flex-col justify-center items-center text-center">
            {!result && !isUploading ? (
              <div className="space-y-6 max-w-md opacity-40">
                <div className="w-24 h-24 rounded-full bg-slate-800 flex items-center justify-center mx-auto">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="12" y1="18" x2="12" y2="12"></line><line x1="9" y1="15" x2="15" y2="15"></line></svg>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-slate-300">Ready for Ingestion</h4>
                  <p className="text-sm text-slate-500 mt-2">Select a DOCX file to begin the automated parsing waterfall. We'll show the quality metrics here.</p>
                </div>
              </div>
            ) : isUploading ? (
              <div className="space-y-8 max-w-md">
                <div className="relative">
                  <div className="w-32 h-32 border-4 border-blue-500/10 border-t-blue-500 rounded-full animate-spin mx-auto"></div>
                  <div className="absolute inset-0 flex items-center justify-center font-bold text-blue-400">AI</div>
                </div>
                <div className="space-y-2">
                  <h4 className="text-xl font-bold text-white animate-pulse">Running Orchestrator...</h4>
                  <p className="text-sm text-slate-400">Scanning document structure, extracting LaTeX formulas, and identifying correct answers across multiple parser instances.</p>
                </div>
                <div className="flex gap-2 justify-center">
                  <span className="w-2 h-2 rounded-full bg-blue-500 animate-bounce"></span>
                  <span className="w-2 h-2 rounded-full bg-blue-500 animate-bounce [animation-delay:-0.15s]"></span>
                  <span className="w-2 h-2 rounded-full bg-blue-500 animate-bounce [animation-delay:-0.3s]"></span>
                </div>
              </div>
            ) : (
              <div className="w-full space-y-8 animate-in zoom-in-95 duration-500">
                <div className="w-24 h-24 rounded-full bg-emerald-500/10 text-emerald-400 flex items-center justify-center mx-auto border border-emerald-500/20">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-white">Import Successful!</h3>
                  <p className="text-slate-400 mt-2">Your questions have been parsed and synced to the Question Bank.</p>
                </div>
                
                <div className="grid grid-cols-3 gap-4 max-w-xl mx-auto">
                  <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800">
                    <span className="block text-2xl font-bold text-blue-400">{result.totalQuestions || 0}</span>
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Questions</span>
                  </div>
                  <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800">
                    <span className="block text-2xl font-bold text-emerald-400">{result.score?.toFixed(1) || 0}%</span>
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Quality</span>
                  </div>
                  <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800">
                    <span className="block text-lg font-bold text-purple-400 truncate px-1" title={result.parserUsed}>{result.parserUsed?.replace('CMSTestParser_', 'P-') || 'N/A'}</span>
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Best Parser</span>
                  </div>
                </div>

                <div className="pt-4">
                  <button 
                    onClick={() => window.location.hash = '#questions'} 
                    className="text-sm font-bold text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-2 mx-auto"
                  >
                    Go to Question Bank
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Defensive helpers for the analyzer result render. The /api/performance/analyze
// response is sometimes "partial" (e.g. ai_insights replaced by a sentinel
// {status: "AI temporarily unavailable", reason: "..."} when GPT-4o is rate-limited
// or the AIPR pipeline late-arrives). The renderer used to assume every array
// field was populated -- a missing aiInsights.lagging_areas would crash with
// `Cannot read properties of undefined (reading 'map')` and the whole panel
// went white. safeArray treats anything non-array as []; AnalyzerErrorBoundary
// is a last-resort recovery panel for any other unforeseen shape change.
const safeArray = (v) => (Array.isArray(v) ? v : []);

class AnalyzerErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error("AnalyzerErrorBoundary caught:", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 16, border: "1px solid #f33", borderRadius: 8, background: "#1a0000", color: "#f88" }}>
          <strong>Analyzer rendering error.</strong>
          <div style={{ marginTop: 8, fontSize: 12, opacity: 0.85 }}>
            The analysis completed, but the UI hit an error rendering the result.
            Try reloading the page; the underlying AIPR result is unaffected.
          </div>
          <details style={{ marginTop: 8, fontSize: 11 }}>
            <summary>Technical detail</summary>
            <pre style={{ whiteSpace: "pre-wrap" }}>{String(this.state.error)}</pre>
          </details>
          <button onClick={() => this.setState({ error: null })} style={{ marginTop: 12 }}>
            Reset
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const PerformanceAnalyzer = () => {
  const [files, setFiles] = useState({ paper: null, answers: null, responses: null });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [isGeneratingBooklet, setIsGeneratingBooklet] = useState(false);
  const [personalizedQuestions, setPersonalizedQuestions] = useState(null);
  const [viewingPracticeFor, setViewingPracticeFor] = useState(null);

  const handleFileChange = (type, file) => {
    setFiles(prev => ({ ...prev, [type]: file }));
  };

  const runAnalysis = async () => {
    if (!files.paper || !files.answers || !files.responses) {
      toast.error("Please upload all three files (DOCX, Answer Key, and Responses).");
      return;
    }

    setIsAnalyzing(true);
    const formData = new FormData();
    formData.append('paperDocx', files.paper);
    formData.append('answersXlsx', files.answers);
    formData.append('responsesXlsx', files.responses);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/performance/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Backend now returns a status envelope:
      //   { status: "success"|"partial"|"user_error"|"transient_error"|"internal_error",
      //     error_code: "...", user_message: "...", data: { ...booklet... } }
      // The HTTP code is also mapped (200 for success/partial, 422/503/500 for errors)
      // so axios only resolves on 200 -- we unwrap envelope.data here.
      const env = res.data || {};
      if (env.status === 'success' || env.status === 'partial') {
        setResults(env.data);
        if (env.status === 'partial') {
          // AI insights unavailable but stats are still usable -- warn rather than success
          toast.info(env.user_message || "AI insights temporarily unavailable. Try again in a minute.");
        } else {
          toast.success("AI Analysis complete!");
        }
        // Task 15 fan-out: PerformanceController kicks off DocxOrchestrator
        // in a background thread on the same DOCX after a successful parse.
        // env.auto_import_queued is set by the controller; its absence on
        // older deploys is a no-op. Wording is "started" (not "imported")
        // because the import is queued, not necessarily complete -- audit
        // log is the source of truth for completion.
        if (env.auto_import_queued === true) {
          toast.info("Question Bank import started ✓", { autoClose: 5000 });
        }
      } else {
        // Defensive: HTTP 200 but unexpected status field -- shouldn't normally happen,
        // since user_error/transient_error/internal_error map to 4xx/5xx and throw.
        toast.error(env.user_message || `Analysis returned unexpected status: ${env.status}`);
      }
    } catch (err) {
      console.error(err);
      // For 422/503/500, axios puts the envelope on err.response.data. Read user_message
      // (new schema) with .message as legacy fallback.
      const env = err.response?.data || {};
      const userMsg = env.user_message || env.message || err.message || "Analysis failed. Please check server logs.";
      toast.error(`Analysis failed: ${userMsg}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const downloadExcel = () => {
    if (!results) return;
    const studentData = Object.entries(results.student_reports).map(([name, data]) => ({
      "Student Name": name,
      "Score": data.score,
      "Total": data.total,
      "Percentage": ((data.score / data.total) * 100).toFixed(1) + "%",
      "Incorrect Qs": data.incorrect_qs.join(", ")
    }));

    const ws = XLSX.utils.json_to_sheet(studentData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Student Performance");
    XLSX.writeFile(wb, "Student_Performance_Report.xlsx");
  };

  const downloadAnswersTemplate = () => {
    const ansWs = XLSX.utils.json_to_sheet([
      { "Question": 1, "Correct Option": "A" },
      { "Question": 2, "Correct Option": "B" }
    ]);
    const ansWb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(ansWb, ansWs, "Answers");
    XLSX.writeFile(ansWb, "Template_Correct_Answers.xlsx");
    toast.info("Answers template downloaded!");
  };

  const downloadResponsesTemplate = () => {
    const resWs = XLSX.utils.json_to_sheet([
      { "Question": 1, "John Doe": "A", "Jane Smith": "B", "Bob Wilson": "A" },
      { "Question": 2, "John Doe": "C", "Jane Smith": "B", "Bob Wilson": "D" }
    ]);
    const resWb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(resWb, resWs, "Responses");
    XLSX.writeFile(resWb, "Template_Student_Responses.xlsx");
    toast.info("Responses template downloaded!");
  };

  const aiInsights = useMemo(() => {
    if (!results?.ai_insights) return null;
    if (typeof results.ai_insights === 'object') return results.ai_insights;
    try {
      return JSON.parse(results.ai_insights);
    } catch (e) {
      console.error("AI Insights Parse Error:", e);
      return null;
    }
  }, [results]);

  const generatePracticeBooklet = async () => {
    if (!results) return;
    setIsGeneratingBooklet(true);
    
    // Prepare data for AI with more context
    const studentDataForAI = {
      class_lagging_topics: aiInsights?.lagging_areas?.map(a => a.topic) || [],
      students: Object.entries(results.student_reports).map(([name, data]) => ({
        name,
        accuracy: (data.score / data.total) * 100,
        feedback: aiInsights?.student_insights?.find(ins => ins.name === name)?.feedback || "",
        weak_question_texts: data.incorrect_qs.map(qNum => results.question_stats[qNum]?.text || "N/A").slice(0, 8)
      }))
    };

    try {
      const uploadToast = toast.loading("AI is analyzing weak topics and generating personalized practice sets...");
      const genRes = await axios.post(`${API_BASE_URL}/api/performance/generate-bulk-practice`, studentDataForAI);
      const questionSets = genRes.data;
      setPersonalizedQuestions(questionSets);
      
      toast.update(uploadToast, { render: "Questions generated! Creating PDF booklet...", type: 'info', isLoading: true });
      
      const bookletData = {
        ai_insights: aiInsights,
        student_sets: questionSets
      };
      
      const pdfRes = await axios.post(`${API_BASE_URL}/api/performance/download-booklet`, bookletData, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([pdfRes.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Personalized_Revision_Booklet.pdf');
      document.body.appendChild(link);
      link.click();
      
      toast.update(uploadToast, { render: "✅ Personalized Revision Booklet downloaded!", type: 'success', isLoading: false, autoClose: 5000 });
    } catch (err) {
      console.error(err);
      let errorMessage;
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          errorMessage = parsed?.message || text;
        } catch {
          errorMessage = err.message;
        }
      } else {
        errorMessage = err.response?.data?.message || err.response?.data || err.message;
      }
      toast.error(`Booklet generation failed: ${errorMessage || "Failed to generate booklet."}`);
    } finally {
      setIsGeneratingBooklet(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-xl font-bold text-white">AI Performance Analyzer</h2>
          <p className="text-sm text-slate-500">Upload paper and responses to get deep pedagogical insights.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Upload Cards */}
        {[
          { id: 'paper', label: 'Question Paper', ext: '.docx', icon: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' },
          { id: 'answers', label: 'Answer Key', ext: '.xlsx', icon: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' },
          { id: 'responses', label: 'Student Responses', ext: '.xlsx', icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' }
        ].map(item => (
          <div key={item.id} className="p-6 rounded-2xl border border-slate-800 bg-slate-900/40 hover:border-blue-500/30 transition-all group relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 text-blue-400 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d={item.icon}></path>
                    {item.id !== 'responses' && <polyline points="14 2 14 8 20 8"></polyline>}
                  </svg>
                </div>
                <h3 className="font-bold text-slate-200">{item.label}</h3>
              </div>
              {item.id !== 'paper' && (
                <button 
                  onClick={item.id === 'answers' ? downloadAnswersTemplate : downloadResponsesTemplate}
                  title="Download Template"
                  className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-blue-400 flex items-center justify-center transition-colors border border-slate-700"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                </button>
              )}
            </div>
            <input 
              type="file" 
              accept={item.ext}
              onChange={(e) => handleFileChange(item.id, e.target.files[0])}
              className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-slate-800 file:text-slate-300 hover:file:bg-slate-700 cursor-pointer"
            />
            {files[item.id] && <p className="mt-2 text-[10px] text-emerald-400 font-medium">✓ {files[item.id].name}</p>}
          </div>
        ))}
      </div>

      <div className="flex justify-center">
        <button 
          onClick={runAnalysis}
          disabled={isAnalyzing}
          className={`px-12 py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-lg shadow-xl shadow-blue-500/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-3 ${isAnalyzing ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          {isAnalyzing ? (
            <>
              <div className="w-5 h-5 border-3 border-white/20 border-t-white rounded-full animate-spin"></div>
              AI Processing...
            </>
          ) : (
            <>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><path d="M9 12l2 2 4-4"></path></svg>
              Run AI Analysis
            </>
          )}
        </button>
      </div>

      {results?.error && (
        <div className="p-6 rounded-2xl border border-rose-500/20 bg-rose-500/5 animate-in shake duration-500">
          <div className="flex items-center gap-3 text-rose-400 font-bold mb-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
            Analysis Error
          </div>
          <p className="text-sm text-slate-400">{results.error}</p>
          {results.traceback && (
            <details className="mt-4">
              <summary className="text-[10px] text-slate-600 uppercase tracking-widest font-bold cursor-pointer hover:text-slate-400 transition-colors">View Technical Details</summary>
              <pre className="mt-2 p-4 rounded-lg bg-slate-950 text-[10px] text-slate-500 overflow-x-auto border border-slate-900 font-mono">
                {results.traceback}
              </pre>
            </details>
          )}
        </div>
      )}

      {results && !results.error && (
        <AnalyzerErrorBoundary>
        <div className="space-y-8 animate-in fade-in zoom-in duration-500">
          {/* Summary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { label: 'Avg. Score', val: results?.overall_metrics?.avg_score?.toFixed(1) || '0.0', sub: 'out of ' + (results?.overall_metrics?.total_questions || 0) },
              { label: 'Total Students', val: results?.overall_metrics?.total_students || 0, sub: 'Responses parsed' },
              { label: 'Total Questions', val: results?.overall_metrics?.total_questions || 0, sub: 'Docx questions' },
              { label: 'Success Rate', val: results?.overall_metrics?.total_questions ? ((results.overall_metrics.avg_score / results.overall_metrics.total_questions) * 100).toFixed(0) + '%' : '0%', sub: 'Average proficiency' }
            ].map((stat, i) => (
              <div key={i} className="p-5 rounded-2xl border border-slate-800 bg-slate-900/60 text-center">
                <span className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">{stat.label}</span>
                <span className="block text-3xl font-black text-white">{stat.val}</span>
                <span className="block text-[10px] text-slate-500 mt-1">{stat.sub}</span>
              </div>
            ))}
          </div>

          {/* AI Insights Card */}
          {aiInsights && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="p-8 rounded-3xl border border-amber-500/20 bg-amber-500/5 backdrop-blur-sm">
                <h3 className="text-xl font-bold text-amber-400 flex items-center gap-3 mb-6">
                  <span className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">🚩</span>
                  Student Lagging Areas
                </h3>
                <div className="space-y-4">
                  {safeArray(aiInsights.lagging_areas).map((area, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2.5 shrink-0"></div>
                      <div>
                        <h4 className="font-bold text-slate-200">{area.topic}</h4>
                        <p className="text-sm text-slate-400 leading-relaxed">{area.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-8 rounded-3xl border border-emerald-500/20 bg-emerald-500/5 backdrop-blur-sm">
                <h3 className="text-xl font-bold text-emerald-400 flex items-center gap-3 mb-6">
                  <span className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">🎯</span>
                  Recommended Focus Areas
                </h3>
                <div className="space-y-4">
                  {safeArray(aiInsights.focus_areas).map((area, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2.5 shrink-0"></div>
                      <div>
                        <h4 className="font-bold text-slate-200">{area.area}</h4>
                        <p className="text-sm text-slate-400 leading-relaxed">{area.action}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Student Table */}
          <div className="rounded-3xl border border-slate-800 bg-slate-900/40 overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-slate-900/60">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                Student-wise Performance Breakdown
              </h3>
              <div className="flex gap-3">
                <button 
                  onClick={generatePracticeBooklet}
                  disabled={isGeneratingBooklet}
                  className={`px-4 py-2 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-sm font-bold border border-blue-500/30 flex items-center gap-2 transition-all ${isGeneratingBooklet ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isGeneratingBooklet ? (
                    <div className="w-4 h-4 border-2 border-blue-400/20 border-t-blue-400 rounded-full animate-spin"></div>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><path d="M9 12l2 2 4-4"></path></svg>
                  )}
                  {isGeneratingBooklet ? "Generating Booklet..." : "Download Personalized Revision Booklet (PDF)"}
                </button>
                <button 
                  onClick={downloadExcel}
                  className="px-4 py-2 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-sm font-bold border border-emerald-500/30 flex items-center gap-2 transition-all"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                  Download Excel Report
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-950/40 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-800">
                  <tr>
                    <th className="px-6 py-4">Student Name</th>
                    <th className="px-6 py-4">Score</th>
                    <th className="px-6 py-4">Accuracy</th>
                    <th className="px-6 py-4">Missed Questions</th>
                    <th className="px-6 py-4">AI Student Insights</th>
                    <th className="px-6 py-4">Practice Set</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {Object.entries(results?.student_reports || {}).map(([name, data], i) => (
                    <tr key={i} className="hover:bg-slate-800/30 transition-colors">
                      <td className="px-6 py-4 text-sm font-bold text-slate-200">{name}</td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-bold text-blue-400">{data?.score || 0}</span>
                        <span className="text-xs text-slate-600"> / {data?.total || 0}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 w-24 bg-slate-800 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${(data?.score/data?.total) > 0.7 ? 'bg-emerald-500' : (data?.score/data?.total) > 0.4 ? 'bg-amber-500' : 'bg-rose-500'}`} 
                              style={{ width: `${((data?.score/data?.total) || 0)*100}%` }}
                            />
                          </div>
                          <span className="text-xs font-bold text-slate-400">{(((data?.score/data?.total) || 0)*100).toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {!data?.incorrect_qs || data.incorrect_qs.length === 0 ? (
                            <span className="text-xs text-emerald-500 font-medium">Full Score! 🌟</span>
                          ) : (
                            data.incorrect_qs.slice(0, 10).map(q => (
                              <span key={q} className="px-1.5 py-0.5 rounded bg-slate-800 text-[10px] text-slate-400 border border-slate-700">Q{q}</span>
                            ))
                          )}
                          {data?.incorrect_qs?.length > 10 && <span className="text-[10px] text-slate-600">+{data.incorrect_qs.length - 10} more</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-xs text-slate-400 italic leading-relaxed max-w-[300px]">
                          {aiInsights?.student_insights?.find(ins => ins.name === name)?.feedback || "Analyzing performance..."}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        {personalizedQuestions && personalizedQuestions[name] ? (
                          <button 
                            onClick={() => setViewingPracticeFor(name)}
                            className="text-[10px] font-bold text-blue-400 hover:text-blue-300 underline underline-offset-4 decoration-blue-400/30"
                          >
                            View Practice Set ({personalizedQuestions[name].length})
                          </button>
                        ) : (
                          <span className="text-[10px] text-slate-600 italic">Generate booklet to see</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Individual Practice Modal */}
          {viewingPracticeFor && personalizedQuestions && personalizedQuestions[viewingPracticeFor] && (
            <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
              <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md" onClick={() => setViewingPracticeFor(null)}></div>
              <div className="relative bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col shadow-2xl animate-in zoom-in-95 duration-200">
                <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-slate-900/60">
                  <div>
                    <h3 className="text-xl font-bold text-white">Personalized Practice: {viewingPracticeFor}</h3>
                    <p className="text-xs text-slate-500 mt-1">Adaptive questions generated based on {viewingPracticeFor}'s performance.</p>
                  </div>
                  <button onClick={() => setViewingPracticeFor(null)} className="p-2 hover:bg-slate-800 rounded-lg transition-colors text-slate-400">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
                  {personalizedQuestions[viewingPracticeFor].map((q, idx) => (
                    <div key={idx} className="space-y-4 pb-8 border-b border-slate-800 last:border-0">
                      <div className="flex items-start gap-4">
                        <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-sm font-bold text-slate-300">{idx + 1}</span>
                        <div className="space-y-4 flex-1">
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${
                              q.difficulty === 'Advanced' ? 'bg-rose-500/10 text-rose-400' : 
                              q.difficulty === 'Remedial' ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400'
                            }`}>
                              {q.difficulty}
                            </span>
                          </div>
                          <p className="text-slate-200 font-medium leading-relaxed">{q.question}</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {Object.entries(q.options).map(([key, val]) => (
                              <div key={key} className={`p-3 rounded-xl border text-sm transition-all ${
                                q.answer === key ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200' : 'bg-slate-950/50 border-slate-800 text-slate-400'
                              }`}>
                                <span className="font-bold mr-2">{key})</span> {val}
                                {q.answer === key && <span className="ml-2 text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded font-bold">CORRECT</span>}
                              </div>
                            ))}
                          </div>
                          <div className="p-4 rounded-xl bg-slate-950/80 border border-slate-800">
                            <span className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">AI Solution</span>
                            <p className="text-xs text-slate-400 leading-relaxed italic">{q.solution}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        </AnalyzerErrorBoundary>
      )}
    </div>
  );
};

const ReviewPaper = () => {
  const [file, setFile] = useState(null);
  const [syllabus, setSyllabus] = useState('');
  const [isReviewing, setIsReviewing] = useState(false);
  const [issues, setIssues] = useState([]);

  const handleReview = async () => {
    if (!file) {
      toast.error("Please upload a DOCX file first.");
      return;
    }

    setIsReviewing(true);
    setIssues([]);
    const formData = new FormData();
    formData.append('file', file);
    if (syllabus.trim()) {
      formData.append('syllabus', syllabus.trim());
    }

    try {
      const res = await axios.post(`${API_BASE_URL}/api/docx/review`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      
      let data = res.data;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch(e) { 
          toast.error("Invalid response from reviewer.");
          return;
        }
      }

      if (data && data.status === 'success') {
        setIssues(data.issues || []);
        toast.success("AI Content Audit complete!");
      } else {
        toast.error(data?.message || 'Review failed - check AI configuration.');
      }
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || "Review failed. Please check server logs.";
      toast.error(`Review failed: ${errorMessage}`);
    } finally {
      setIsReviewing(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-xl font-bold text-white">AI Question Paper Reviewer</h2>
          <p className="text-sm text-slate-500">Verify question accuracy, concepts, and syllabus alignment before publishing.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-6">
          {/* File Upload Card */}
          <div className="p-6 rounded-3xl border border-slate-800 bg-slate-900/40 hover:border-blue-500/30 transition-all group relative">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 text-blue-400 flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
              </div>
              <div>
                <h3 className="font-bold text-slate-200">Question Paper</h3>
                <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">DOCX Format Only</p>
              </div>
            </div>
            <input 
              type="file" 
              accept=".docx"
              onChange={(e) => setFile(e.target.files[0])}
              className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-slate-800 file:text-slate-300 hover:file:bg-slate-700 cursor-pointer"
            />
            {file && <p className="mt-2 text-[10px] text-emerald-400 font-medium">✓ {file.name}</p>}
          </div>

          {/* Syllabus Input Card */}
          <div className="p-6 rounded-3xl border border-slate-800 bg-slate-900/40 hover:border-purple-500/30 transition-all group">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 text-purple-400 flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>
              </div>
              <div>
                <h3 className="font-bold text-slate-200">Exam Syllabus</h3>
                <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">For Out-of-Syllabus Check</p>
              </div>
            </div>
            <textarea 
              value={syllabus}
              onChange={(e) => setSyllabus(e.target.value)}
              placeholder="Paste syllabus topics here (e.g. Mechanics, Thermodynamics, Optics...)"
              className="w-full bg-slate-950/50 border border-slate-800 rounded-xl p-4 text-sm text-slate-200 placeholder:text-slate-700 focus:border-purple-500/50 outline-none transition-all min-h-[200px] resize-y custom-scrollbar"
            />
          </div>

          <button 
            onClick={handleReview}
            disabled={isReviewing || !file}
            className={`w-full py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-lg shadow-xl shadow-blue-500/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-3 ${isReviewing || !file ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {isReviewing ? (
              <>
                <div className="w-5 h-5 border-3 border-white/20 border-t-white rounded-full animate-spin"></div>
                AI Auditing...
              </>
            ) : (
              <>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><path d="M9 12l2 2 4-4"></path></svg>
                Analyze Paper
              </>
            )}
          </button>
        </div>

        {/* Results Area */}
        <div className="lg:col-span-2">
          <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6 min-h-[600px] relative overflow-hidden flex flex-col">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 blur-[80px] rounded-full"></div>
            
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-800 relative z-10">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <span className="text-blue-400">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg>
                </span>
                Audit Issues Breakdown
              </h3>
              {issues.length > 0 && (
                <span className="px-3 py-1 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20 text-xs font-bold uppercase tracking-widest">
                  {issues.length} Issues Found
                </span>
              )}
            </div>

            <div className="flex-1 relative z-10">
              {!isReviewing && issues.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-8 space-y-4 opacity-40">
                  <div className="w-20 h-20 rounded-full bg-slate-800 flex items-center justify-center">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                  </div>
                  <h4 className="text-xl font-bold text-slate-300">Ready for Audit</h4>
                  <p className="text-sm text-slate-500 max-w-xs">Upload a paper and optionally provide a syllabus to start the AI-powered content review.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {issues.map((issue, idx) => (
                    <div key={idx} className="group p-5 rounded-2xl border border-slate-800 bg-slate-950/40 hover:border-slate-700 transition-all animate-in slide-in-from-right-4 duration-300" style={{ animationDelay: `${idx * 50}ms` }}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest border ${
                            issue.category === 'CONCEPTUAL' ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' :
                            issue.category === 'OUT_OF_SYLLABUS' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' :
                            issue.category === 'TYPO' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                            issue.category === 'CORRECT_ANSWER' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                            'bg-slate-800 text-slate-400 border-slate-700'
                          }`}>
                            {issue.category?.replace('_', ' ')}
                          </span>
                          <span className="text-xs font-bold text-slate-500">Question: {issue.question}</span>
                        </div>
                      </div>
                      <h4 className="text-slate-200 font-medium leading-relaxed">{issue.issue}</h4>
                      {issue.fix && (
                        <div className="mt-4 p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
                          <div className="flex items-center gap-2 text-[10px] font-bold text-emerald-500 uppercase tracking-widest mb-1">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                            Suggested Fix
                          </div>
                          <p className="text-sm text-emerald-200/80 italic">{issue.fix}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function CMSTest() {
  const [activeTab, setActiveTab] = useState('questions');
  const [counts, setCounts] = useState({ videos: 0, materials: 0, questions: 0 });
  const [waMobile, setWaMobile] = useState('');
  const [waSending, setWaSending] = useState(false);
  const [waLastResult, setWaLastResult] = useState(null); // { ok, message }
  const [waStudySending, setWaStudySending] = useState(false);
  const [waStudyResult, setWaStudyResult] = useState(null); // { ok, message }

  const sendWhatsAppTest = async () => {
    if (!WA_ACCESS_TOKEN || !WA_PHONE_NUMBER_ID) {
      toast.error('WhatsApp env vars missing. Set REACT_APP_WA_ACCESS_TOKEN and REACT_APP_WA_PHONE_NUMBER_ID in pen/.env.local and restart the dev server.');
      return;
    }
    // Strip + , spaces, dashes — Meta wants digits only with country code.
    const cleaned = waMobile.replace(/[^0-9]/g, '');
    if (cleaned.length < 10) {
      toast.error('Enter a valid mobile number with country code (e.g. 919876543210)');
      return;
    }

    setWaSending(true);
    setWaLastResult(null);
    try {
      const url = `https://graph.facebook.com/${WA_GRAPH_VERSION}/${WA_PHONE_NUMBER_ID}/messages`;
      const payload = {
        messaging_product: 'whatsapp',
        to: cleaned,
        type: 'template',
        template: {
          name: WA_DEFAULT_TEMPLATE,
          language: { code: WA_DEFAULT_LANG }
        }
      };
      const res = await waAxios.post(url, payload, {
        headers: {
          Authorization: `Bearer ${WA_ACCESS_TOKEN}`,
          'Content-Type': 'application/json'
        }
      });
      const msgId = res.data?.messages?.[0]?.id || 'sent';
      setWaLastResult({ ok: true, message: `Sent • id ${msgId.slice(0, 14)}…` });
      toast.success(`WhatsApp test sent to +${cleaned}`);
    } catch (err) {
      const apiErr = err.response?.data?.error;
      const msg = apiErr ? `${apiErr.code}: ${apiErr.message}` : (err.message || 'Send failed');
      setWaLastResult({ ok: false, message: msg });
      toast.error(`WhatsApp send failed: ${msg}`);
    } finally {
      setWaSending(false);
    }
  };

  const sendStudyTimeTemplate = async () => {
    if (!WA_ACCESS_TOKEN || !WA_PHONE_NUMBER_ID) {
      toast.error('WhatsApp env vars missing. Set REACT_APP_WA_ACCESS_TOKEN and REACT_APP_WA_PHONE_NUMBER_ID in pen/.env.local and restart the dev server.');
      return;
    }
    // Use whatever the user typed in the mobile input; fall back to the test number.
    const cleaned = (waMobile.replace(/[^0-9]/g, '') || WA_STUDY_TEST_TO);
    if (cleaned.length < 10) {
      toast.error('Enter a valid mobile number with country code (e.g. 919876543210)');
      return;
    }

    setWaStudySending(true);
    setWaStudyResult(null);
    try {
      const url = `https://graph.facebook.com/${WA_STUDY_GRAPH_VERSION}/${WA_PHONE_NUMBER_ID}/messages`;
      const payload = {
        messaging_product: 'whatsapp',
        to: cleaned,
        type: 'template',
        template: {
          name: WA_STUDY_TEMPLATE,
          language: { code: WA_STUDY_LANG },
          components: [
            {
              type: 'body',
              parameters: [
                { type: 'text', text: WA_STUDY_PARAMS.name },
                { type: 'text', text: WA_STUDY_PARAMS.subject1 },
                { type: 'text', text: WA_STUDY_PARAMS.mins1 },
                { type: 'text', text: WA_STUDY_PARAMS.subject2 },
                { type: 'text', text: WA_STUDY_PARAMS.mins2 },
                { type: 'text', text: WA_STUDY_PARAMS.subject3 },
                { type: 'text', text: WA_STUDY_PARAMS.mins3 }
              ]
            }
          ]
        }
      };
      const res = await waAxios.post(url, payload, {
        headers: {
          Authorization: `Bearer ${WA_ACCESS_TOKEN}`,
          'Content-Type': 'application/json'
        }
      });
      const msgId = res.data?.messages?.[0]?.id || 'sent';
      setWaStudyResult({ ok: true, message: `Sent • id ${msgId.slice(0, 14)}…` });
      toast.success(`study_time sent to +${cleaned}`);
    } catch (err) {
      const apiErr = err.response?.data?.error;
      const msg = apiErr ? `${apiErr.code}: ${apiErr.message}` : (err.message || 'Send failed');
      setWaStudyResult({ ok: false, message: msg });
      toast.error(`study_time send failed: ${msg}`);
    } finally {
      setWaStudySending(false);
    }
  };

  React.useEffect(() => {
    Promise.all([
      axios.get(API_BASE_URL + '/api/files/type/Video').catch(() => ({ data: [] })),
      axios.get(API_BASE_URL + '/api/files/type/PDF').catch(() => ({ data: [] })),
      axios.get(API_BASE_URL + '/api/questions-test/count').catch(() => ({ data: 0 }))
    ]).then(([vidRes, matRes, qCountRes]) => {
      setCounts({
        videos: vidRes.data ? vidRes.data.length : 0,
        materials: matRes.data ? matRes.data.length : 0,
        questions: qCountRes.data || 0
      });
    });
  }, []);

  const tabs = [
    { id: 'questions', label: 'Question Banks', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg> },
    { id: 'videos', label: 'Video Lectures', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"></polygon><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect></svg> },
    { id: 'materials', label: 'Study Material', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg> },
    { id: 'performance', label: 'Performance Analyzer', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"></path><path d="M22 12A10 10 0 0 0 12 2v10z"></path></svg> },
    { id: 'review', label: 'Review Paper', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><path d="M9 12l2 2 4-4"></path></svg> },
    { id: 'import_docx', label: 'Import DOCX', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg> },
  ];

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 pb-12">
      <style>{inlineImageStyle}</style>
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
            Content Management System
          </h1>
          <p className="mt-2 text-lg text-slate-400 max-w-2xl">
            Upload and organize video lectures, study materials, and assessments. Features versioning and chapter tagging.
          </p>
        </div>
        
        {/* Quick Stats */}
        <div className="flex items-center gap-4 bg-slate-900/50 p-2 rounded-xl border border-slate-800">
          <div className="px-4 py-2 text-center border-r border-slate-700/50">
            <span className="block text-2xl font-bold text-white">{counts.videos}</span>
            <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">Videos</span>
          </div>
          <div className="px-4 py-2 text-center border-r border-slate-700/50">
            <span className="block text-2xl font-bold text-white">{counts.materials}</span>
            <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">Notes</span>
          </div>
          <div className="px-4 py-2 text-center">
            <span className="block text-2xl font-bold text-white">{counts.questions}</span>
            <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">MCQs</span>
          </div>
        </div>
      </div>

      {/* WhatsApp Test Panel (Meta Cloud API) */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-slate-900/50 border border-emerald-700/40 rounded-xl p-4">
        <div className="flex items-center gap-2 text-emerald-400 font-semibold text-sm">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          WhatsApp Test
        </div>
        <input
          type="tel"
          value={waMobile}
          onChange={(e) => setWaMobile(e.target.value)}
          placeholder="Mobile with country code, e.g. 919876543210"
          className="flex-1 min-w-0 bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500"
          disabled={waSending}
        />
        <button
          type="button"
          onClick={sendWhatsAppTest}
          disabled={waSending}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap"
        >
          {waSending ? 'Sending…' : `Send "${WA_DEFAULT_TEMPLATE}"`}
        </button>
        <button
          type="button"
          onClick={sendStudyTimeTemplate}
          disabled={waStudySending}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap"
        >
          {waStudySending ? 'Sending…' : `Send POC ${WA_STUDY_TEMPLATE}`}
        </button>
        {waLastResult && (
          <span className={`text-xs font-medium ${waLastResult.ok ? 'text-emerald-400' : 'text-red-400'}`}>
            {waLastResult.message}
          </span>
        )}
        {waStudyResult && (
          <span className={`text-xs font-medium ${waStudyResult.ok ? 'text-indigo-400' : 'text-red-400'}`}>
            {waStudyResult.message}
          </span>
        )}
      </div>
      
      {/* Navigation Tabs */}
      <div className="border-b border-slate-800">
        <nav className="-mb-px flex space-x-8 overflow-x-auto scrollbar-none" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-all duration-300
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-700'
                }
              `}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content Area */}
      <div className="pt-2">
        {activeTab === 'videos' && <VideoLectures />}
        {activeTab === 'materials' && <StudyMaterials />}
        {activeTab === 'questions' && <QuestionBanks setActiveTab={setActiveTab} />}
        {activeTab === 'performance' && <PerformanceAnalyzer />}
        {activeTab === 'review' && <ReviewPaper />}
        {activeTab === 'import_docx' && <ImportDocx />}
      </div>
    </div>
  );
}
